import {
  AndamentoCorreiosContextoLote,
  AndamentoCorreiosDocumento,
  AndamentoCorreiosResponse,
  AndamentoCorreiosResumo,
  CorreiosOcorrencia,
  CorreiosStatusEntrega,
  CorreiosTentativaEntrega,
} from '../models/andamento-correios.model';
import { TratamentoLoteTagSeverity } from '../models/tratamento-lote.model';

/** Ordem lógica do fluxo de entrega para ordenação client-side. */
export const CORREIOS_STATUS_ENTREGA_ORDEM: readonly CorreiosStatusEntrega[] = [
  'Aguardando postagem',
  'Postado',
  'Em trânsito',
  'Saiu para entrega',
  'Tentativa de entrega',
  'Aguardando retirada',
  'Entrega realizada',
  'Endereço insuficiente',
  'Destinatário ausente',
  'Devolvido ao remetente',
];

export const CORREIOS_STATUS_ENTREGA_OPTIONS = CORREIOS_STATUS_ENTREGA_ORDEM.map((status) => ({
  label: status,
  value: status,
}));

export function correiosStatusOrdemIndice(status: string): number {
  const idx = CORREIOS_STATUS_ENTREGA_ORDEM.indexOf(status as CorreiosStatusEntrega);
  return idx >= 0 ? idx : CORREIOS_STATUS_ENTREGA_ORDEM.length;
}

export function correiosStatusSeverity(status: string): TratamentoLoteTagSeverity {
  switch (status) {
    case 'Entrega realizada':
      return 'success';
    case 'Postado':
    case 'Em trânsito':
    case 'Saiu para entrega':
      return 'info';
    case 'Tentativa de entrega':
    case 'Aguardando retirada':
    case 'Aguardando confirmação':
    case 'Aguardando postagem':
      return 'warn';
    case 'Endereço insuficiente':
    case 'Destinatário ausente':
    case 'Devolvido ao remetente':
    case 'Com pendências':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function formatarDataHoraCorreios(dataHora: string | null | undefined): string {
  if (!dataHora) {
    return '-';
  }

  const data = new Date(dataHora);
  if (Number.isNaN(data.getTime())) {
    return '-';
  }

  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Códigos Correios usados na timeline e no status. */
const CODIGOS_ENTREGUE = new Set(['BDE']);
const CODIGOS_TRANSITO = new Set(['RO', 'DO', 'PO', 'OEC', 'PAR']);
const CODIGOS_ERRO = new Set(['AE', 'LDI', 'BDI', 'FC']);

export function isCodigoCorreiosEntregue(codigo: string | null | undefined, descricao = ''): boolean {
  if (!CODIGOS_ENTREGUE.has((codigo ?? '').toUpperCase())) {
    return false;
  }
  const desc = descricao.toLowerCase();
  if (!desc) {
    return true;
  }
  return desc.includes('entregue') && !desc.includes('não entregue') && !desc.includes('nao entregue');
}

export function isCodigoCorreiosTransito(codigo: string | null | undefined): boolean {
  const cd = (codigo ?? '').toUpperCase();
  return CODIGOS_TRANSITO.has(cd);
}

export function isCodigoCorreiosErro(codigo: string | null | undefined, descricao = ''): boolean {
  const cd = (codigo ?? '').toUpperCase();
  if (CODIGOS_ERRO.has(cd)) {
    return true;
  }
  if (cd === 'BDE') {
    const desc = descricao.toLowerCase();
    return (
      desc.includes('não entregue') ||
      desc.includes('nao entregue') ||
      desc.includes('devolvido') ||
      desc.includes('desconhecido') ||
      desc.includes('ausente')
    );
  }
  return false;
}

/** Normaliza a resposta dos endpoints Rastreio / RastreioLote para o modelo da UI. */
export function mapRastreioApiResponse(
  raw: unknown,
  contexto: AndamentoCorreiosContextoLote = {}
): AndamentoCorreiosResponse {
  const record = asRecord(raw);
  const documentosRaw = record
    ? pickArray(record, 'documentos', 'Documentos', 'itens', 'Itens', 'rastreios', 'Rastreios')
    : Array.isArray(raw)
      ? raw
      : [];

  let documentos = documentosRaw
    .map((item) => mapRastreioDocumento(item))
    .filter((item): item is AndamentoCorreiosDocumento => !!item);

  if (!documentos.length) {
    const documentoUnico = mapRastreioDocumento(raw);
    if (documentoUnico) {
      documentos = [documentoUnico];
    }
  }

  const resumo = mapResumoAndamento(record, documentos);

  const dataConsultaRaiz = pickString(
    record,
    'dataUltimaConsulta',
    'DataUltimaConsulta',
    'dataConsulta',
    'DataConsulta',
    'dataAtualizacao',
    'DataAtualizacao'
  );

  const datasConsultaDocumentos = documentosRaw
    .map((item) => {
      const doc = asRecord(item);
      return pickString(doc, 'dataConsulta', 'DataConsulta', 'dataUltimaConsulta', 'DataUltimaConsulta');
    })
    .filter(Boolean)
    .sort((a, b) => dataHoraCorreiosMs(b) - dataHoraCorreiosMs(a));

  return {
    idLote:
      pickNumber(record, 'idLote', 'IdLote', 'idAtividadeToiLote', 'IdAtividadeToiLote') ||
      contexto.idLote ||
      contexto.idAtividadeToiLote ||
      0,
    nrLoteCorreios:
      pickString(record, 'nrLoteCorreios', 'NrLoteCorreios', 'nrLote', 'NrLote') ||
      contexto.nrLoteCorreios ||
      '',
    dataUltimaConsulta: dataConsultaRaiz || datasConsultaDocumentos[0] || new Date().toISOString(),
    resumo,
    documentos,
  };
}

export function mapRastreioDocumento(raw: unknown): AndamentoCorreiosDocumento | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const historico = pickArray(
    record,
    'historico',
    'Historico',
    'ocorrencias',
    'Ocorrencias',
    'eventos',
    'Eventos'
  )
    .map((item) => mapOcorrenciaCorreios(item))
    .filter((item): item is CorreiosOcorrencia => !!item);

  const tentativasApi = pickArray(record, 'tentativas', 'Tentativas')
    .map((item, index) => mapTentativaEntrega(item, index))
    .filter((item): item is CorreiosTentativaEntrega => !!item);

  const tentativas =
    tentativasApi.length > 0 ? tentativasApi : derivarTentativasDoHistorico(historico);

  // Fonte de verdade: eventos/histórico. Campo solto da API só como fallback.
  const ultimaOcorrencia =
    obterUltimaOcorrenciaDoHistorico(historico) ??
    mapOcorrenciaCorreios(
      record['ultimaOcorrencia'] ??
        record['UltimaOcorrencia'] ??
        record['ultimoEvento'] ??
        record['UltimoEvento']
    );

  const statusBruto = pickString(
    record,
    'statusEntrega',
    'StatusEntrega',
    'nmStatusEntrega',
    'NmStatusEntrega',
    'status',
    'Status',
    'nmStatus',
    'NmStatus'
  );

  const idAtividade = pickNumber(record, 'idAtividade', 'IdAtividade');
  const idAtividadeToi = pickNumber(record, 'idAtividadeToi', 'IdAtividadeToi');
  const numeroBa = pickString(record, 'numeroBa', 'NumeroBa', 'os', 'Os', 'nrOs', 'NrOs');
  const nrDocumento = pickString(record, 'nrDocumento', 'NrDocumento', 'documento', 'Documento');
  // API RastreioLote usa cdRastreio como código do objeto/AR.
  const nrAr =
    pickString(record, 'nrAr', 'NrAr', 'numeroAr', 'NumeroAr', 'cdRastreio', 'CdRastreio') || null;

  if (!numeroBa && !nrDocumento && !idAtividade && !idAtividadeToi) {
    return null;
  }

  const qtdTentativasApi = pickNumber(
    record,
    'qtdTentativas',
    'QtdTentativas',
    'quantidadeTentativas',
    'QuantidadeTentativas'
  );

  return {
    idAtividade: idAtividade || idAtividadeToi,
    numeroBa,
    nrDocumento,
    nrAr,
    statusEntrega: resolverStatusEntrega(statusBruto, ultimaOcorrencia),
    qtdTentativas: qtdTentativasApi || tentativas.length,
    tentativas,
    ultimaOcorrencia,
    historico,
  };
}

export function calcularResumoAndamentoCorreios(
  documentos: AndamentoCorreiosDocumento[]
): AndamentoCorreiosResumo {
  const entregues = documentos.filter((doc) => doc.statusEntrega === 'Entrega realizada').length;
  const comErro = documentos.filter((doc) =>
    ['Endereço insuficiente', 'Devolvido ao remetente', 'Destinatário ausente'].includes(
      doc.statusEntrega
    )
  ).length;
  const pendentes = documentos.length - entregues - comErro;

  const ultimaOcorrencia = obterUltimaOcorrenciaDoHistorico(
    documentos
      .map((doc) => doc.ultimaOcorrencia)
      .filter((ocorrencia): ocorrencia is CorreiosOcorrencia => !!ocorrencia)
  );

  let statusEntregaLote: AndamentoCorreiosResumo['statusEntregaLote'] = 'Em trânsito';
  if (comErro > 0) {
    statusEntregaLote = 'Com pendências';
  } else if (entregues === documentos.length && documentos.length > 0) {
    statusEntregaLote = 'Entrega realizada';
  } else if (entregues > 0) {
    statusEntregaLote = 'Em trânsito';
  } else if (documentos.every((doc) => doc.statusEntrega === 'Aguardando postagem')) {
    statusEntregaLote = 'Aguardando confirmação';
  }

  return {
    statusEntregaLote,
    ultimaOcorrencia,
    qtdTentativasTotal: documentos.reduce((acc, doc) => acc + doc.qtdTentativas, 0),
    qtdDocumentosComAr: documentos.filter((doc) => !!doc.nrAr).length,
    qtdEntregues: entregues,
    qtdPendentes: pendentes,
    qtdComErro: comErro,
  };
}

/** O resumo do backend tem prioridade; o cálculo local completa os campos ausentes. */
function mapResumoAndamento(
  raw: Record<string, unknown> | null,
  documentos: AndamentoCorreiosDocumento[]
): AndamentoCorreiosResumo {
  const calculado = calcularResumoAndamentoCorreios(documentos);
  if (!raw) {
    return calculado;
  }

  const statusBruto = pickString(
    raw,
    'statusEntregaLote',
    'StatusEntregaLote',
    'statusLote',
    'StatusLote',
    'nmStatusLote',
    'NmStatusLote'
  );

  return {
    statusEntregaLote: (statusBruto ||
      calculado.statusEntregaLote) as AndamentoCorreiosResumo['statusEntregaLote'],
    // Sempre derivado dos eventos dos documentos; campo solto do lote só como fallback.
    ultimaOcorrencia:
      calculado.ultimaOcorrencia ??
      mapOcorrenciaCorreios(
        raw['ultimaOcorrencia'] ??
          raw['UltimaOcorrencia'] ??
          raw['ultimoEvento'] ??
          raw['UltimoEvento']
      ),
    qtdTentativasTotal:
      pickNumber(raw, 'qtdTentativasTotal', 'QtdTentativasTotal') || calculado.qtdTentativasTotal,
    qtdDocumentosComAr:
      pickNumber(raw, 'qtdDocumentosComAr', 'QtdDocumentosComAr') || calculado.qtdDocumentosComAr,
    qtdEntregues: pickNumber(raw, 'qtdEntregues', 'QtdEntregues') || calculado.qtdEntregues,
    qtdPendentes: pickNumber(raw, 'qtdPendentes', 'QtdPendentes') || calculado.qtdPendentes,
    qtdComErro: pickNumber(raw, 'qtdComErro', 'QtdComErro') || calculado.qtdComErro,
  };
}

function mapOcorrenciaCorreios(raw: unknown): CorreiosOcorrencia | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const descricao = pickString(record, 'descricao', 'Descricao', 'dsEvento', 'DsEvento');
  if (!descricao) {
    return null;
  }

  const cidade = pickString(record, 'cidade', 'Cidade', 'nmCidade', 'NmCidade');
  const uf = pickString(record, 'uf', 'Uf', 'UF', 'sgUf', 'SgUf');
  const localExplicito = pickString(record, 'local', 'Local', 'dsLocal', 'DsLocal');
  const local =
    localExplicito ||
    (cidade && uf ? `${cidade}/${uf}` : cidade || uf) ||
    null;

  return {
    // Não fabricar "agora": data inventada distorce a escolha da última ocorrência.
    dataHora: pickString(
      record,
      'dataHora',
      'DataHora',
      'dataEvento',
      'DataEvento',
      'dtEvento',
      'DtEvento'
    ),
    // API RastreioLote usa "codigo" (ex.: BDE, OEC, PO).
    codigoEvento: pickString(
      record,
      'codigoEvento',
      'CodigoEvento',
      'codigo',
      'Codigo',
      'cdEvento',
      'CdEvento'
    ).toUpperCase(),
    descricao,
    local,
    detalhe: pickString(record, 'detalhe', 'Detalhe', 'dsDetalhe', 'DsDetalhe') || null,
  };
}

function mapTentativaEntrega(raw: unknown, index: number): CorreiosTentativaEntrega | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const motivo = pickString(record, 'motivo', 'Motivo', 'dsMotivo', 'DsMotivo');
  const resultadoRaw = pickString(record, 'resultado', 'Resultado', 'nmResultado', 'NmResultado');
  const resultado: CorreiosTentativaEntrega['resultado'] =
    resultadoRaw.toLowerCase() === 'entregue' ? 'Entregue' : 'Não entregue';

  return {
    numero: pickNumber(record, 'numero', 'Numero', 'nrTentativa', 'NrTentativa') || index + 1,
    dataHora:
      pickString(record, 'dataHora', 'DataHora', 'dataTentativa', 'DataTentativa') ||
      new Date().toISOString(),
    motivo: motivo || resultadoRaw || 'Tentativa de entrega',
    resultado,
  };
}

function mapStatusEntregaPorLabel(raw: string): CorreiosStatusEntrega | null {
  const normalizado = raw.trim().toLowerCase();
  if (!normalizado) {
    return null;
  }

  const matchExato = CORREIOS_STATUS_ENTREGA_ORDEM.find(
    (status) => status.toLowerCase() === normalizado
  );
  if (matchExato) {
    return matchExato;
  }

  if (
    normalizado.includes('entregue') &&
    !normalizado.includes('não entregue') &&
    !normalizado.includes('nao entregue')
  ) {
    return 'Entrega realizada';
  }
  if (normalizado.includes('saiu para entrega')) {
    return 'Saiu para entrega';
  }
  if (normalizado.includes('postado')) {
    return 'Postado';
  }
  if (normalizado.includes('trânsito') || normalizado.includes('transito')) {
    return 'Em trânsito';
  }
  if (normalizado.includes('retirada') || normalizado.includes('aguardando na unidade')) {
    return 'Aguardando retirada';
  }
  if (normalizado.includes('devolvido')) {
    return 'Devolvido ao remetente';
  }
  if (
    normalizado.includes('endereço insuficiente') ||
    normalizado.includes('endereco insuficiente')
  ) {
    return 'Endereço insuficiente';
  }
  if (
    normalizado.includes('ausente') ||
    normalizado.includes('desconhecido') ||
    normalizado.includes('não entregue') ||
    normalizado.includes('nao entregue')
  ) {
    if (normalizado.includes('ausente')) {
      return 'Destinatário ausente';
    }
    return 'Tentativa de entrega';
  }
  if (normalizado.includes('aguardando postagem')) {
    return 'Aguardando postagem';
  }

  return null;
}

function mapStatusEntregaPorCodigo(codigo: string, descricao: string): CorreiosStatusEntrega | null {
  const cd = codigo.toUpperCase();
  const desc = descricao.toLowerCase();

  switch (cd) {
    case 'BDE':
      if (desc.includes('devolvido')) {
        return 'Devolvido ao remetente';
      }
      if (
        desc.includes('não entregue') ||
        desc.includes('nao entregue') ||
        desc.includes('desconhecido') ||
        desc.includes('ausente')
      ) {
        return desc.includes('ausente') ? 'Destinatário ausente' : 'Tentativa de entrega';
      }
      return 'Entrega realizada';
    case 'OEC':
      return 'Saiu para entrega';
    case 'PO':
      return 'Postado';
    case 'RO':
    case 'DO':
    case 'PAR':
      return 'Em trânsito';
    case 'LDI':
      return 'Aguardando retirada';
    case 'AE':
    case 'BDI':
      return desc.includes('ausente') ? 'Destinatário ausente' : 'Tentativa de entrega';
    case 'FC':
      return 'Endereço insuficiente';
    default:
      return null;
  }
}

function resolverStatusEntrega(
  statusBruto: string,
  ultima: CorreiosOcorrencia | null
): CorreiosStatusEntrega {
  const porLabelApi = mapStatusEntregaPorLabel(statusBruto);
  if (porLabelApi) {
    return porLabelApi;
  }

  if (ultima) {
    const porCodigo = mapStatusEntregaPorCodigo(ultima.codigoEvento, ultima.descricao);
    if (porCodigo) {
      return porCodigo;
    }
    const porDescricao = mapStatusEntregaPorLabel(ultima.descricao);
    if (porDescricao) {
      return porDescricao;
    }
  }

  return historicoVazioStatus(statusBruto);
}

function historicoVazioStatus(statusBruto: string): CorreiosStatusEntrega {
  return statusBruto.trim() ? 'Em trânsito' : 'Aguardando postagem';
}

/** Cada saída para entrega (OEC) conta como uma tentativa. */
function derivarTentativasDoHistorico(historico: CorreiosOcorrencia[]): CorreiosTentativaEntrega[] {
  const ordenado = [...historico].sort(
    (a, b) => dataHoraCorreiosMs(a.dataHora) - dataHoraCorreiosMs(b.dataHora)
  );

  const tentativas: CorreiosTentativaEntrega[] = [];
  let numero = 0;

  for (let i = 0; i < ordenado.length; i++) {
    const evento = ordenado[i];
    if (evento.codigoEvento.toUpperCase() !== 'OEC') {
      continue;
    }

    numero += 1;
    const posteriores = ordenado.slice(i + 1);
    const entregueDepois = posteriores.some(
      (e) =>
        e.codigoEvento.toUpperCase() === 'BDE' &&
        /entregue/i.test(e.descricao) &&
        !/não entregue|nao entregue/i.test(e.descricao)
    );
    const falhaDepois = posteriores.find(
      (e) =>
        isCodigoCorreiosErro(e.codigoEvento) ||
        /não entregue|nao entregue|ausente|desconhecido|endereço insuficiente|endereco insuficiente/i.test(
          e.descricao
        )
    );

    tentativas.push({
      numero,
      dataHora: evento.dataHora,
      motivo:
        falhaDepois?.descricao ||
        falhaDepois?.detalhe ||
        evento.detalhe ||
        evento.descricao ||
        'Tentativa de entrega',
      resultado: entregueDepois ? 'Entregue' : 'Não entregue',
    });
  }

  return tentativas;
}

function dataHoraCorreiosMs(dataHora: string): number {
  const ms = new Date(dataHora).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/** Última ocorrência = evento com data/hora mais recente no histórico. */
function obterUltimaOcorrenciaDoHistorico(
  historico: CorreiosOcorrencia[]
): CorreiosOcorrencia | null {
  if (!historico.length) {
    return null;
  }

  return [...historico].sort(
    (a, b) => dataHoraCorreiosMs(b.dataHora) - dataHoraCorreiosMs(a.dataHora)
  )[0];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function pickString(record: Record<string, unknown> | null | undefined, ...keys: string[]): string {
  if (!record) {
    return '';
  }

  for (const key of keys) {
    const value = record[key];
    if (value == null) {
      continue;
    }
    const texto = String(value).trim();
    if (texto) {
      return texto;
    }
  }

  return '';
}

function pickNumber(record: Record<string, unknown> | null | undefined, ...keys: string[]): number {
  if (!record) {
    return 0;
  }

  for (const key of keys) {
    const value = record[key];
    if (value == null || value === '') {
      continue;
    }
    const numero = Number(value);
    if (Number.isFinite(numero)) {
      return numero;
    }
  }

  return 0;
}

function pickArray(record: Record<string, unknown> | null | undefined, ...keys: string[]): unknown[] {
  if (!record) {
    return [];
  }

  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
}
